/**
 * An app account for an expert who has none (`POST /api/users/provision`, `user.provision`): never a Microsoft tenant
 * account; it links at the person's first sign-in. An existing account comes back as it is. When the server answers
 * with a notice (the account is inactive), the dialog shows it and the person is not added.
 */

import { useState } from 'react';
import { Info } from 'lucide-react';

import { UserProvisionIn, userProvisionInDisplayNameMax } from '@/api/generated/schemas';
import { useProvisionUser } from '@/api/queries';
import type { ProvisionedUser } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

export function ProvisionDialog({
  onClose,
  onProvisioned,
}: {
  onClose: () => void;
  onProvisioned: (user: ProvisionedUser, note: string | null) => void;
}) {
  const provision = useProvisionUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const valid = Object.keys(fieldErrors(UserProvisionIn, { email, display_name: name })).length === 0;
  const server = serverFieldErrors(provision.error);

  const submit = () =>
    provision.mutate(
      { email: email.trim(), display_name: name.trim() },
      {
        onSuccess: (user) => {
          if (user.notice) setNotice(user.notice);
          else onProvisioned(user, user.signed_in ? null : 'not signed in yet');
        },
      },
    );

  return (
    <Modal
      title="Add a person who is not in the app"
      subtitle="They get an app account that links to their Microsoft sign-in the first time they sign in."
      size="narrow"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(provision)} label="Add person" pendingLabel="Adding…" disabled={!valid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(provision.error, Object.keys(UserProvisionIn.shape))} />
        {notice && (
          <div className="alert alert-info form-alert" role="status">
            <Info aria-hidden="true" />
            <div className="a-body">{notice}</div>
          </div>
        )}
        <div className="field">
          <label htmlFor="provision-email">Work e-mail</label>
          <input id="provision-email" type="email" required autoFocus value={email} {...fieldA11y('provision-email', server.email)} onChange={(e) => setEmail(e.target.value)} />
          <FieldError inputId="provision-email" message={server.email} />
        </div>
        <div className="field">
          <label htmlFor="provision-name">Name</label>
          <input id="provision-name" type="text" required maxLength={userProvisionInDisplayNameMax} value={name} {...fieldA11y('provision-name', server.display_name)} onChange={(e) => setName(e.target.value)} />
          <FieldError inputId="provision-name" message={server.display_name} />
        </div>
      </div>
    </Modal>
  );
}
